'use client';

import { useWriteContract, useWaitForTransactionReceipt, usePublicClient } from 'wagmi';
import { erc20Abi, parseUnits, getAddress, isAddress } from 'viem';
import type { Address } from 'viem';
import { ChainType } from '@/constants/chain-specific';
import { showTransactionToast, showTransactionError, TX_ACTIONS } from '@/util/transactionToast';
import toast from '@/util/toast';
import { useCallback, useEffect } from 'react';
interface UseSendLPTokenProps {
  lpTokenAddress?: Address;
  chainId?: ChainType;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}
export function useSendLPToken({
  lpTokenAddress,
  chainId,
  onSuccess,
  onError
}: UseSendLPTokenProps) {
  const publicClient = usePublicClient({
    chainId
  });
  const {
    writeContractAsync,
    data: hash,
    isPending,
    error: writeError
  } = useWriteContract();
  const {
    isLoading: isConfirming,
    isSuccess,
    error: receiptError
  } = useWaitForTransactionReceipt({
    hash
  });
  const sendLPTokens = useCallback(async (params: {
    to: string;
    amount: string;
    decimals: number;
  }) => {
    const {
      to,
      amount,
      decimals
    } = params;

    // Validate inputs
    if (!lpTokenAddress) {
      const error = new Error('LP token address not provided');
      onError?.(error);
      toast.error('LP token address not provided');
      return;
    }
    if (!to) {
      const error_0 = new Error('Recipient address is required');
      onError?.(error_0);
      toast.error('Recipient address is required');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      const error_1 = new Error('Amount must be greater than 0');
      onError?.(error_1);
      toast.error('Amount must be greater than 0');
      return;
    }

    // Validate and format address
    let recipientAddress: Address;
    try {
      recipientAddress = getAddress(to);
      if (!isAddress(recipientAddress)) {
        throw new Error('Invalid address format');
      }
    } catch (e) {
      const error_2 = new Error('Invalid recipient address');
      onError?.(error_2);
      toast.error('Invalid recipient address');
      return;
    }
    try {
      // Parse amount with token decimals
      const parsedAmount = parseUnits(amount, decimals);

      // Create the transaction promise
      const txPromise = writeContractAsync({
        address: lpTokenAddress,
        abi: erc20Abi,
        chainId: chainId ? Number(chainId) : undefined,
        functionName: 'transfer',
        args: [recipientAddress, parsedAmount]
      }).then(async hash_0 => {
        // Wait for transaction confirmation
        const receipt = await publicClient?.waitForTransactionReceipt({
          hash: hash_0
        });
        return {
          hash: hash_0,
          receipt
        };
      });

      // Show transaction toast that tracks the promise
      await showTransactionToast(txPromise, {
        action: TX_ACTIONS.TRANSFER_LP,
        chain: publicClient?.chain,
        showProgress: true
      });

      // Call success callback
      onSuccess?.();
    } catch (error_3) {
      const err = error_3 as Error;
      onError?.(err);
      // Error toast is already shown by showTransactionToast
      // Only show additional error if it's not a user rejection
      if (!err.message.includes('User rejected') && !err.message.includes('User denied')) {
        showTransactionError(err, TX_ACTIONS.TRANSFER_LP);
      }
    }
  }, [lpTokenAddress, chainId, writeContractAsync, publicClient, onSuccess, onError]);
  return {
    sendLPTokens,
    isLoading: isPending || isConfirming,
    isSuccess,
    error: writeError || receiptError,
    hash
  };
}